import React, { useContext, useEffect, useState } from 'react';

import { makeStyles } from '@material-ui/core/styles';
import Avatar from '@material-ui/core/Avatar';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button'
import Container from '@material-ui/core/Container';
import CssBaseline from '@material-ui/core/CssBaseline';
import Grid from '@material-ui/core/Grid';
import Link from '@material-ui/core/Link';
import PersonAddIcon from '@material-ui/icons/PersonAdd';
import Typography from '@material-ui/core/Typography';

import Copyright from '../Copyright';
import TextField from '@material-ui/core/TextField'
import FireDataContext from '../../services/firebase/FireDataContext';
import { List, ListItem } from '@material-ui/core';
import AddressForm from './AddressForm';
import { keyGen } from '../../services/keygen';
import AuthContext from '../../services/firebase/AuthContext';
import { timestamp } from '../../app/data/validate';

const useStyles = makeStyles((theme) => ({
    paper: {
        marginTop: theme.spacing(8),
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    avatar: {
        margin: theme.spacing(1),
        backgroundColor: theme.palette.secondary.main,
    },
    form: {
        width: '100%', // Fix IE 11 issue.
        marginTop: theme.spacing(3),
    },
    submit: {
        margin: theme.spacing(3, 0, 2),
    },
}));

const SelectOrganizationPage = () => {
    const db = useContext(FireDataContext);
    const { currentUser } = useContext(AuthContext);
    const classes = useStyles();
    const [orgName, setOrgName] = useState('');

    const [organizations, setOrganizations] = useState([]);
    const [selectedOrg, setSelectedOrg] = useState();
    const [matches, setMatches] = useState([]);
    const [adding, setAdding] = useState(false);
    const [address, setAddress] = useState({ lines: [], postcode: '' });

    useEffect(() => {
        db.get({ path: 'organizations' })
            .then(data => {
                setOrganizations(data
                    .filter(o => o.type === 'cais-client' && o.id !== '0000' && o.name?.trim().length > 0)
                    .map(o => {
                        return {
                            id: o.id,
                            name: o.name?.trim() ?? '',
                            address: o.address ?? { lines: ['', '', '', ''], postcode: '' }
                        }
                    })
                    .sort((a, b) => a.name.localeCompare(b.name))
                );
            })
            .catch(error => console.log(error));

    }, [db])


    const handleNameCHange = value => {
        const criteria = value ?? '';
        setOrgName(criteria);

        // find all the names that start with the input text
        const startsWith = new RegExp(`^${criteria}`, 'i');
        const good = criteria.length ? organizations.filter(o => startsWith.test(o.name)) : [];

        // find all the other names that just contain the input text
        const containsCriteria = new RegExp(`${criteria}`, 'i');

        // don't bother with the contains query if there's only one character!
        const reasonable = criteria.length > 1 ? organizations
            .filter(o => !good.find(g => g.id === o.id))
            .filter(o => containsCriteria.test(o.name)) : [];

        const combined = [...good, ...reasonable];

        setMatches(combined);
        setAdding(combined.length === 0)
        setSelectedOrg(undefined);
    };

    const handleAddressChange = updated => {
        setAddress(updated)
    }

    const handleAddOrg = () => {
        setAdding(true);
    }

    const handleUseOrg = org => {
        setSelectedOrg(org);
        setAddress(org.address);
        setAdding(true);
        setOrgName(org.name)
    }

    const handleActivate = () => {
        const id = selectedOrg?.id ?? keyGen(10);

        (() => {
            return new Promise(async (resolve, reject) => {
                try {
                    var org = await db.get({ path: `organizations/${id}` });
                    const user = await db.get({ path: `users/${currentUser.uid}` });

                    if (!org) {
                        org = {
                            name: orgName,
                            adminOrganization: 'WHuoyDs7zcB8wUU7euiC',
                            phoneNumber: user?.phone ?? '',
                            type: 'cais-client',
                            address,
                            ts: timestamp(),
                            uid: currentUser.uid,
                        }
                        
                        await db.update({
                            path: `organizations/${id}`,
                            payload: org
                        })
                    }
                    
                    // update the user's org so they can get out of limbo :)
                    await db.update({
                        path: `users/${currentUser.uid}`,
                        payload: {
                            organization: id,
                            ts: timestamp()
                        }
                    });

                    // this org/member info should be deprecated - it was required when we used to add referrers to firms before they signed up but ...
                    // ... now we have self-service sign-up for solicitors so ...
                    await db.update({
                        path: `organizations/${id}/members/${currentUser.uid}`,
                        payload: {
                            email: currentUser.email,
                            uid: currentUser.uid,
                            ts: timestamp(),
                        }
                    });

                    await db.remove({ path: `organizations/0000/members/${currentUser.uid}` });
                    
                    currentUser.organizationId = id;
                    
                    resolve(true);
                }
                catch (error) {
                    reject(error);
                }
            })
        })()
            // navigate didn't seem to trigger a refresh of App routes so let's brute force the issue :O 
            .then(() => {
                window.location.replace("/")
            });

    }


    return (
        <Container component="main" maxWidth="xs">
            <CssBaseline />
            <div className={classes.paper}>
                <Typography component="h1" variant="h6">The Intermediary Cooperative</Typography>
                <Avatar className={classes.avatar}>
                    <PersonAddIcon />
                </Avatar>
                <Typography component="h1" variant="h5">Complete Sign Up</Typography>
                <div className={classes.form}>
                    <Grid container spacing={2} justify="center">
                        <Grid item >
                            <Typography variant="h6" color="initial">Who do you work for?</Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Your organisation or firm name"
                                value={orgName}
                                onChange={({ target: { value } }) => handleNameCHange(value)}
                                placeholder='Enter your organisation name'
                                fullWidth
                                variant="outlined"
                            />
                        </Grid>
                        {
                            (!adding && orgName) &&
                            <Grid item xs={12}>
                                <Button onClick={handleAddOrg} fullWidth variant="contained" color="primary">add your organisation</Button>
                            </Grid>
                        }
                        {
                            !adding &&
                            <Grid item xs={12}>
                                {
                                    matches?.length > 0 &&
                                    <Typography variant="subtitle2">Possible matches ...</Typography>
                                }
                                <List>
                                    {
                                        matches.map(org => {
                                            return <ListItem onClick={() => handleUseOrg(org)} key={org.id} button >{org.name}</ListItem>
                                        })
                                    }
                                </List>
                            </Grid>
                        }
                        {
                            (adding || (!matches?.length && orgName)) ?
                                <Grid item xs={12}>
                                    <AddressForm address={address} onChange={handleAddressChange} />
                                </Grid>
                                : <></>
                        }
                        {
                            (adding && address?.lines[0] && address?.postcode) &&
                            <Grid item xs={12}>
                                <Button onClick={handleActivate} fullWidth variant="contained" color="secondary">Activate your account</Button>
                            </Grid>
                        }
                    </Grid>
                    <Grid container justify="flex-end" style={{ marginTop: '1rem' }}>
                        <Grid item>
                            <Link href="/signin" variant="body2">Already have an account? Sign in</Link>
                        </Grid>
                    </Grid>
                </div>
            </div>
            <Box mt={5}>
                <Copyright />
            </Box>
        </Container>
    );

}

const SelectOrganizationPagePath = '/signup/other';

export {
    SelectOrganizationPagePath
}

export default SelectOrganizationPage;